import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const DateSelectionModal = ({ isOpen, onClose, onConfirm, loading, tripData }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState('');

  // Calculate minimum date (today)
  const today = new Date()?.toISOString()?.split('T')?.[0];

  // Calculate suggested end date based on trip duration
  const getSuggestedEndDate = (start) => {
    if (!start || !tripData?.itinerary?.length) return '';
    
    const startDateObj = new Date(start);
    const daysToAdd = tripData?.itinerary?.length - 1 || 0;
    const endDateObj = new Date(startDateObj);
    endDateObj?.setDate(startDateObj?.getDate() + daysToAdd);
    
    return endDateObj?.toISOString()?.split('T')?.[0];
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    setError('');
    
    // ✅ FIXED: ALWAYS auto-populate end date based on trip duration when start date changes
    if (date) {
      const suggested = getSuggestedEndDate(date);
      setEndDate(suggested);
    } else {
      // Clear end date if start date is cleared
      setEndDate('');
    }
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    setError('');
  };

  const validateDates = () => {
    if (!startDate) {
      setError('Please select a start date for your trip.');
      return false;
    }
    
    if (!endDate) {
      setError('Please select an end date for your trip.');
      return false;
    }
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (end < start) {
      setError('End date must be after start date.');
      return false;
    }
    
    // Check if date range matches expected trip duration
    const daysDifference = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    const expectedDays = tripData?.itinerary?.length || 1;
    
    if (daysDifference < expectedDays) {
      setError(`Your trip itinerary is ${expectedDays} days long. Please select a date range of at least ${expectedDays} days.`);
      return false;
    }
    
    return true;
  };

  const handleConfirm = () => {
    if (validateDates()) {
      onConfirm({ startDate, endDate });
    }
  };

  const handleClose = () => {
    setStartDate('');
    setEndDate('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-organic max-w-md w-full p-6 shadow-organic-lg">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary/10 rounded-organic flex items-center justify-center">
              <Icon name="Calendar" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h3 className="font-heading font-heading-bold text-lg text-card-foreground">
                Select Trip Dates
              </h3>
              <p className="text-sm text-muted-foreground">
                When will you be taking this adventure?
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-muted rounded-organic-sm transition-colors"
            disabled={loading}
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {/* Trip Duration Info */}
        <div className="mb-6 p-3 bg-primary/10 rounded-organic">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Info" size={16} color="var(--color-primary)" />
            <span className="text-sm font-body font-body-medium text-card-foreground">
              Trip Duration
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Your itinerary is planned for {tripData?.itinerary?.length || 0} days. 
            The dates you select should match this duration.
          </p>
        </div>

        {/* Date Selection Form */}
        <div className="space-y-4">
          {/* Start Date - ✅ FIXED: Added wrapper div with relative positioning and cursor-pointer */}
          <div>
            <label className="block text-sm font-body font-body-medium text-card-foreground mb-2">
              Start Date
            </label>
            <div className="relative">
              <input
                type="date"
                value={startDate}
                onChange={(e) => handleStartDateChange(e?.target?.value)}
                min={today}
                className="w-full px-3 py-2 border border-border rounded-organic focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent cursor-pointer"
                disabled={loading}
                style={{ colorScheme: 'light' }}
              />
              {/* ✅ FIXED: Add calendar icon hint when input is empty */}
              {!startDate && !loading && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <Icon name="Calendar" size={16} color="var(--color-muted-foreground)" />
                </div>
              )}
            </div>
          </div>

          {/* End Date - ✅ FIXED: Added wrapper div with relative positioning */}
          <div>
            <label className="block text-sm font-body font-body-medium text-card-foreground mb-2">
              End Date
            </label>
            <div className="relative">
              <input
                type="date"
                value={endDate}
                onChange={(e) => handleEndDateChange(e?.target?.value)}
                min={startDate || today}
                className="w-full px-3 py-2 border border-border rounded-organic focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent cursor-pointer bg-muted/30"
                disabled={loading}
                style={{ colorScheme: 'light' }}
                readOnly
              />
              {/* ✅ FIXED: Show auto-populated indicator */}
              {!endDate && !loading && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <Icon name="Calendar" size={16} color="var(--color-muted-foreground)" />
                </div>
              )}
              {endDate && startDate && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                </div>
              )}
            </div>
            {/* ✅ FIXED: Add helper text explaining auto-population */}
            {startDate && endDate && (
              <p className="text-xs text-muted-foreground mt-1 flex items-center space-x-1">
                <Icon name="Sparkles" size={12} color="var(--color-primary)" />
                <span>Automatically set based on {tripData?.itinerary?.length || 0}-day itinerary</span>
              </p>
            )}
          </div>

          {/* Date Range Summary */}
          {startDate && endDate && (
            <div className="p-3 bg-success/10 rounded-organic">
              <div className="flex items-center space-x-2 mb-1">
                <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                <span className="text-sm font-body font-body-medium text-success">
                  Trip Duration: {Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1} days
                </span>
              </div>
              <p className="text-xs text-muted-foreground ml-6">
                {new Date(startDate)?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} - {new Date(endDate)?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-organic">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} color="var(--color-destructive)" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3 mt-6">
          <button
            onClick={handleClose}
            disabled={loading}
            className="flex-1 bg-muted text-muted-foreground py-2 px-4 rounded-organic hover:bg-muted/80 transition-colors font-body font-body-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading || !startDate || !endDate}
            className="flex-1 bg-primary text-primary-foreground py-2 px-4 rounded-organic hover:bg-primary/90 transition-colors font-body font-body-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {loading && <Icon name="Loader" size={16} className="animate-spin" />}
            <span>{loading ? 'Saving...' : 'Confirm Dates'}</span>
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-4 p-3 bg-muted/30 rounded-organic">
          <p className="text-xs text-muted-foreground">
            💡 <strong>Tip:</strong> You can edit these dates later from your trip detail page if your plans change.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DateSelectionModal;